import { parse } from '@babel/parser'
import type {
  Expression,
  JSXAttribute,
  JSXElement,
  JSXExpressionContainer,
  ObjectExpression,
  ObjectProperty,
  ReturnStatement,
  Statement,
} from '@babel/types'
import {
  isSupportedTag,
  type CanvasNode,
  type NodeKind,
  type StudioDocument,
  type StyleMap,
  type StyleValue,
} from './model'

const INDENT = '  '

function styleValueToCode(value: StyleValue): string {
  return typeof value === 'number' ? String(value) : JSON.stringify(value)
}

function styleToCode(styles: StyleMap, depth: number): string {
  const entries = Object.entries(styles)
  if (entries.length === 0) return '{{}}'

  const propertyIndent = INDENT.repeat(depth + 2)
  const closingIndent = INDENT.repeat(depth + 1)
  const properties = entries
    .map(([key, value]) => `${propertyIndent}${key}: ${styleValueToCode(value)},`)
    .join('\n')

  return `{{\n${properties}\n${closingIndent}}}`
}

function nodeToJsx(
  document: StudioDocument,
  nodeId: string,
  depth: number,
): string {
  const node = document.nodes[nodeId]
  if (!node) throw new Error(`Missing node: ${nodeId}`)

  const indent = INDENT.repeat(depth)
  const attrs = [
    `data-node-id=${JSON.stringify(node.id)}`,
    `data-node-kind=${JSON.stringify(node.kind)}`,
    `style=${styleToCode(node.styles, depth)}`,
  ]
  const opening = `<${node.tag} ${attrs.join(' ')}`
  const text = node.text === undefined ? '' : `{${JSON.stringify(node.text)}}`

  if (node.children.length === 0) {
    return `${indent}${opening}>${text}</${node.tag}>`
  }

  const children = node.children
    .map((childId) => nodeToJsx(document, childId, depth + 1))
    .join('\n')

  return `${indent}${opening}>\n${children}\n${indent}</${node.tag}>`
}

export function compileDocument(document: StudioDocument): string {
  const jsx = nodeToJsx(document, document.rootId, 2)

  return `/**
 * App.tsx is reconciled from Driftless' shared document graph.
 * Stable data-node-id attributes connect code edits back to canvas nodes.
 */
export default function NorthstarLaunch() {
  return (
${jsx}
  )
}
`
}

function getAttribute(
  element: JSXElement,
  name: string,
): JSXAttribute | undefined {
  return element.openingElement.attributes.find(
    (attribute): attribute is JSXAttribute =>
      attribute.type === 'JSXAttribute' &&
      attribute.name.type === 'JSXIdentifier' &&
      attribute.name.name === name,
  )
}

function stringAttribute(element: JSXElement, name: string): string | undefined {
  const attribute = getAttribute(element, name)
  if (!attribute?.value) return undefined
  return attribute.value.type === 'StringLiteral'
    ? attribute.value.value
    : undefined
}

function expressionValue(expression: Expression | null): StyleValue | undefined {
  if (!expression) return undefined
  if (expression.type === 'StringLiteral') return expression.value
  if (expression.type === 'NumericLiteral') return expression.value
  if (expression.type === 'UnaryExpression' && expression.operator === '-') {
    if (expression.argument.type === 'NumericLiteral') {
      return -expression.argument.value
    }
  }
  return undefined
}

function objectPropertyName(property: ObjectProperty): string | undefined {
  if (property.key.type === 'Identifier') return property.key.name
  if (property.key.type === 'StringLiteral') return property.key.value
  return undefined
}

function parseStyle(element: JSXElement): StyleMap {
  const attribute = getAttribute(element, 'style')
  if (!attribute?.value || attribute.value.type !== 'JSXExpressionContainer') {
    return {}
  }

  const container = attribute.value as JSXExpressionContainer
  if (container.expression.type !== 'ObjectExpression') return {}

  const styles: StyleMap = {}
  for (const property of (container.expression as ObjectExpression).properties) {
    if (property.type !== 'ObjectProperty') continue
    const key = objectPropertyName(property)
    if (!key) continue
    const value = expressionValue(property.value as Expression)
    if (value !== undefined) styles[key] = value
  }
  return styles
}

function parseText(element: JSXElement): string | undefined {
  const parts: string[] = []
  for (const child of element.children) {
    if (child.type === 'JSXText' && child.value.trim()) {
      parts.push(child.value.trim())
    }
    if (
      child.type === 'JSXExpressionContainer' &&
      child.expression.type === 'StringLiteral'
    ) {
      parts.push(child.expression.value)
    }
  }
  return parts.length > 0 ? parts.join(' ') : undefined
}

function humanizeId(id: string): string {
  return id
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/^./, (character) => character.toUpperCase())
}

function findReturnedJsx(statements: Statement[]): JSXElement | undefined {
  for (const statement of statements) {
    if (
      statement.type === 'ExportDefaultDeclaration' &&
      statement.declaration.type === 'FunctionDeclaration'
    ) {
      const returned = statement.declaration.body.body.find(
        (entry): entry is ReturnStatement => entry.type === 'ReturnStatement',
      )
      if (returned?.argument?.type === 'JSXElement') return returned.argument
    }

    if (statement.type === 'FunctionDeclaration') {
      const returned = statement.body.body.find(
        (entry): entry is ReturnStatement => entry.type === 'ReturnStatement',
      )
      if (returned?.argument?.type === 'JSXElement') return returned.argument
    }
  }
  return undefined
}

function elementTag(element: JSXElement): string {
  const name = element.openingElement.name
  if (name.type !== 'JSXIdentifier') {
    throw new Error('Only intrinsic HTML elements are supported in the canvas.')
  }
  return name.name
}

function parseElement(
  element: JSXElement,
  previous: StudioDocument,
  nodes: Record<string, CanvasNode>,
): string {
  const id = stringAttribute(element, 'data-node-id')
  if (!id) {
    throw new Error('Every canvas element needs a stable data-node-id attribute.')
  }
  if (nodes[id]) throw new Error(`Duplicate data-node-id: ${id}`)

  const tag = elementTag(element)
  if (!isSupportedTag(tag)) {
    throw new Error(`The <${tag}> element is not supported by this renderer.`)
  }

  const previousNode = previous.nodes[id]
  const declaredKind = stringAttribute(element, 'data-node-kind')
  const kind = (declaredKind ?? previousNode?.kind ?? 'group') as NodeKind
  const childIds = element.children
    .filter((child): child is JSXElement => child.type === 'JSXElement')
    .map((child) => parseElement(child, previous, nodes))

  nodes[id] = {
    id,
    name: previousNode?.name ?? humanizeId(id),
    tag,
    kind,
    text: parseText(element),
    styles: parseStyle(element),
    children: childIds,
  }

  return id
}

export interface ParseDocumentResult {
  document?: StudioDocument
  error?: string
  parseMs: number
}

export function parseDocumentCode(
  source: string,
  previous: StudioDocument,
): ParseDocumentResult {
  const startedAt = performance.now()
  try {
    const ast = parse(source, {
      sourceType: 'module',
      plugins: ['jsx', 'typescript'],
      errorRecovery: false,
    })
    const rootElement = findReturnedJsx(ast.program.body)
    if (!rootElement) {
      throw new Error('The default component must return one JSX root element.')
    }

    const nodes: Record<string, CanvasNode> = {}
    const rootId = parseElement(rootElement, previous, nodes)
    return {
      document: {
        ...previous,
        rootId,
        nodes,
        updatedAt: Date.now(),
      },
      parseMs: performance.now() - startedAt,
    }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unable to parse code.',
      parseMs: performance.now() - startedAt,
    }
  }
}
